import React, { useState, useMemo } from 'react';
import _get from 'lodash/get';
import { StacklineColor, useStacklineTheme, SlMenu, SlDropdownMenuOption } from '@stackline/ui';
import { CommonSummaryInfoSubtitle } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import LegendWithMetric, { LegendWithMetricProps } from '../Legend/LegendWithMetric';
import { AggregationField } from 'src/types/application/types';
import CommonSummaryWithDropdown from 'src/components/BeaconV2Components/common/CommonSummaryWithDropdown';
import { useStyles } from 'src/components/BeaconV2Components/EntityTableRefresh/styles';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import IconButton from '@mui/material/IconButton';
import { SlDropdownMenu, SlDropdownMenuProps } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import Box from '@mui/material/Box';
import domtoimage from 'src/utils/dom-to-imagev2';
import saveAs from 'file-saver';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { useSnackbar } from 'src/utils/Hooks/typedHooks';
import { defaultImageOption } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useCsvExporter';

export interface BeaconChartWithLegendProps {
  children: React.ReactNode;
  title?: React.ReactNode;
  comparisonLegendProps?: Omit<LegendWithMetricProps, 'color'> & { color?: StacklineColor } & { opacity?: number };
  primaryLegendProps?: Omit<LegendWithMetricProps, 'color'> & { color?: StacklineColor };
  groupByFields?: React.ComponentProps<typeof CommonSummaryWithDropdown>['groupByFields'];
  selectedGroupBy?: AggregationField;
  handleGroupBy?: (id: string) => void;
  handleMainMetric?: (id: string) => void;
  mainMetricFields?: React.ComponentProps<typeof CommonSummaryWithDropdown>['mainMetricFields'];
  selectedMainMetric?: AggregationField;
  selectedGroupByField?: string;
  loading?: boolean;
  loadingComponent?: React.ReactNode;
  subtitle?: string | React.ReactNode;
  disableExportCsv?: boolean;
  convertSeriesToDelimitedData?: () => Promise<any>;
  /**
   * Diplays the primary legend indicator as dashed when true
   */
  primaryDashed?: boolean;
  /**
   * Color for the secondary legend indicator
   */
  secondaryColor?: StacklineColor;
  showTitleSummary?: boolean;
  singleDropdownProps?: SlDropdownMenuProps & { prefix?: string } & { suffix?: string };
  /**
   * Optional builder for the chart dropdown options. Should be combined with
   * `dropdownOptionHandler` to handle the click event. Passes in the default
   * dropdown options and returns the new options.
   */
  buildDropdownMenuOptions?: (options: SlDropdownMenuOption[]) => SlDropdownMenuOption[];
  /**
   * Optional handler for the dropdown options. Can be combined with
   * `buildDropdownMenuOptions` to build the options if adding
   * custom options.
   */

  dropdownOptionHandler?: (option: SlDropdownMenuOption, defaultDropdownHandler: () => Promise<void>) => void;
  showExportMenu?: boolean;
  saveImageRef?: React.RefObject<HTMLDivElement>;

  customLegends?: React.ReactElement;
  includeSummaryInfo?: boolean;
  subtitleSX?: React.CSSProperties;
  containerStyles?: React.CSSProperties;
}

/**
 * Generic container for charts with a legend. The chart to render should be passed in as a child.
 */
const BeaconChartWithLegend = ({
  children,
  comparisonLegendProps,
  primaryLegendProps,
  title = '',
  groupByFields,
  handleGroupBy,
  handleMainMetric,
  selectedGroupBy,
  mainMetricFields,
  selectedMainMetric,
  loading,
  subtitle,
  loadingComponent = <SplineChartLoading />,
  subtitleSX,
  primaryDashed,
  secondaryColor,
  disableExportCsv = false,
  showTitleSummary = true,
  singleDropdownProps,
  convertSeriesToDelimitedData,
  buildDropdownMenuOptions,
  dropdownOptionHandler,
  showExportMenu = true,
  customLegends,
  includeSummaryInfo = true,
  /**
   * Ref to the container to save as an image
   */
  saveImageRef,
  containerStyles = {}
}: BeaconChartWithLegendProps) => {
  const classes = useStyles();
  const theme = useStacklineTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { showSnackbar } = useSnackbar();

  const handleOpenExports = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const chartContainerRef = React.useRef<HTMLDivElement>(null);

  const handleDropdownClick = (option: SlDropdownMenuOption) => {
    const defaultDropdownHandler = async () => {
      try {
        const file_name =
          (typeof title === 'string' ? title : null) || _get(selectedMainMetric, 'displayName') || 'chart';
        switch (option.id) {
          case 'saveImage':
          case 'copyImage': {
            window.focus();
            // Wait for focus and potential rendering delays
            await new Promise((resolve) => setTimeout(resolve, 200));

            const maxWaitTime = 2000;
            const pollInterval = 100;
            const startTime = Date.now();

            while (!chartContainerRef.current && Date.now() - startTime < maxWaitTime) {
              await new Promise((resolve) => setTimeout(resolve, pollInterval));
            }

            // Wait for the ref to become available, with max waiting time of 2 seconds
            if (!chartContainerRef.current) {
              console.error(`Chart container ref not available after ${maxWaitTime}ms`);
              showSnackbar({ type: 'error', message: 'Error copying image: Chart not ready.' });
              return;
            }
            const blob = (await domtoimage.toBlob(
              saveImageRef?.current ?? (chartContainerRef.current as HTMLDivElement),
              defaultImageOption
            )) as Blob;

            if (!blob) {
              console.error(`domtoimage.toBlob returned null for chart: ${title} chart`);
              showSnackbar({ type: 'error', message: 'Failed to generate image for copying.' });
              return;
            }

            if (option.id === 'saveImage') {
              saveAs(blob, `${file_name}.png`);
            } else if (option.id === 'copyImage') {
              if (blob) {
                const clipboardItem = new ClipboardItem({ 'image/png': blob });
                await navigator.clipboard.write([clipboardItem]);
                showSnackbar({ type: 'success', message: 'Image copied to clipboard!' });
              } else {
                console.error(`Unable to generate blob for chart: ${title}`);
              }
            }
            setAnchorEl(null);
            break;
          }

          case 'exportCsv': {
            if (convertSeriesToDelimitedData) {
              // Export as csv
              await convertSeriesToDelimitedData();
            }
            setAnchorEl(null);
            break;
          }

          default:
        }
      } catch (e) {
        console.error('Error exporting chart:', e);
      }
    };

    if (dropdownOptionHandler) {
      dropdownOptionHandler(option, defaultDropdownHandler);
    } else {
      defaultDropdownHandler();
    }
  };

  const handleCloseExports = () => {
    setAnchorEl(null);
  };

  const exportButton = showExportMenu ? (
    <IconButton
      onClick={handleOpenExports}
      sx={{
        height: theme.spacing.mdl,
        width: theme.spacing.mdl,
        borderRadius: theme.spacing.xs,
        color: theme.colors.primary
      }}
    >
      <MoreHorizIcon />
    </IconButton>
  ) : null;

  const dropdownOptions: SlDropdownMenuOption[] = useMemo(() => {
    const defaultOptions: SlDropdownMenuOption[] = [
      {
        id: 'saveImage',
        label: 'Save image'
      },
      {
        id: 'copyImage',
        label: 'Copy image'
      },
      ...(disableExportCsv
        ? []
        : [
            {
              id: 'exportCsv',
              label: 'Export CSV'
            }
          ])
    ];
    if (buildDropdownMenuOptions) {
      return buildDropdownMenuOptions(defaultOptions);
    }
    return defaultOptions;
  }, [disableExportCsv, buildDropdownMenuOptions]);

  return (
    <div style={{ backgroundColor: 'white', ...containerStyles }} ref={chartContainerRef}>
      <Flex gap={subtitle && !loading ? 0 : 'md'} flexDirection="column">
        {showTitleSummary && (
          <Box
            marginBottom={subtitle ? theme.spacing.none : theme.spacing.md}
            // if we remove summaryInfo (All Categories, Keyword List), Title and subtitle has to be "8px")
            sx={{ transform: includeSummaryInfo || subtitle ? 'none' : 'translateY(8px)' }}
          >
            <Flex justifyContent="space-between">
              {groupByFields && groupByFields.length > 1 ? (
                <Flex flexDirection="column">
                  <CommonSummaryWithDropdown
                    prefixTitle={`${title} `}
                    handleGroupByChange={(id) => handleGroupBy(id)}
                    groupByFields={groupByFields}
                    selectedGroupBy={selectedGroupBy}
                    mainMetricFields={mainMetricFields}
                    handleMainMetric={handleMainMetric}
                    selectedMainMetric={selectedMainMetric}
                    shouldShowFullItemText
                  />
                  <CommonSummaryInfoSubtitle title="" includeSubtitle={includeSummaryInfo} />
                </Flex>
              ) : (
                <CommonSummaryInfoSubtitle
                  title={
                    singleDropdownProps ? (
                      <div className={classes.entityTableHeaderContainer}>
                        <Flex gap="sm" alignItems="center">
                          {singleDropdownProps?.prefix && <Text variant="h4">{singleDropdownProps?.prefix}</Text>}
                          <SlDropdownMenu {...singleDropdownProps} buttonVariant="title" shouldShowFullItemText />
                          {singleDropdownProps?.suffix && <Text variant="h4">{singleDropdownProps?.suffix}</Text>}
                        </Flex>
                      </div>
                    ) : (
                      title
                    )
                  }
                  includeSubtitle={includeSummaryInfo}
                />
              )}
              {!loading && exportButton}
            </Flex>
          </Box>
        )}
        {!loading && subtitle && typeof subtitle === 'string' && (
          <Text variant="body1" sx={{ marginBottom: theme.spacing.md, ...subtitleSX }}>
            {subtitle}
          </Text>
        )}
        {!loading && subtitle && typeof subtitle !== 'string' && subtitle}
        <Flex flexDirection="column" gap="lg">
          {loading ? (
            loadingComponent
          ) : (
            <Flex gap="lg" flexDirection="column">
              <Flex justifyContent="space-between">
                <Flex gap="lg">
                  {primaryLegendProps && (
                    <>
                      {comparisonLegendProps && (
                        <LegendWithMetric {...comparisonLegendProps} color={secondaryColor || 'casper'} />
                      )}
                      <LegendWithMetric
                        {...primaryLegendProps}
                        color={primaryLegendProps.color || 'primary'}
                        dashed={primaryDashed}
                      />
                    </>
                  )}
                </Flex>
                {!showTitleSummary && exportButton}
              </Flex>
              {customLegends}
              {children}
            </Flex>
          )}
        </Flex>
      </Flex>
      <SlMenu
        anchorEl={anchorEl}
        open={!!anchorEl}
        selectedId={null}
        onChange={handleDropdownClick}
        onClose={handleCloseExports}
        options={dropdownOptions}
      />
    </div>
  );
};

export default BeaconChartWithLegend;
